import { createReducer, on } from '@ngrx/store';
import moment from 'moment';

import { DiagStackDtcActions } from '../diag-stack-dtc/diag-stack-dtc.actions';
import {
  ExtendedParametersTabsEnum,
  RemoteDiagnosticsUseCasesActions,
} from '../remote-diagnostics-use-cases';

import { RemoteDiagnosticsActions } from './remote-diagnostics.actions';
import {
  DIAGNOSTICS_CONNECTION_STATUS,
  DIAGNOSTICS_CONNECTION_STEP,
  DIAGNOSTICS_SUPPORTED_INTERVALS,
  RemoteDiagnosticsParameter,
} from './remote-diagnostics.model';
import { RemoteDiagnosticsState } from './remote-diagnostics.state';

export const initRemoteDiagnosticsState: RemoteDiagnosticsState = {
  activeConnectionStep: DIAGNOSTICS_CONNECTION_STEP.WARNING,
  connectionStatus: DIAGNOSTICS_CONNECTION_STATUS.INACTIVE,
  activeSessions: [],
  supportedIntervals: Object.keys(DIAGNOSTICS_SUPPORTED_INTERVALS)
    .filter((value) => !isNaN(Number(value)))
    .map(Number),
  loading: false,
  logicalLinkList: null,
  sessionStatusChecked: false,
  activeSessionStatusChecked: false,
  scheduleMultipleParametersReadCycleInProgress: false,
  cancelMultipleParametersReadCycleInProgress: false,
  connectionReason: null,
  isUseCasePopupBlocked: false,
  logicalLinkOpenStatus: {
    loading: false,
    error: null,
  },
  vddListStatus: {
    loading: false,
    error: null,
  },
  isChartUncollapsed: false,
};

export const remoteDiagnosticsReducer = createReducer(
  initRemoteDiagnosticsState,

  on(
    RemoteDiagnosticsActions.setConnectionStatus,
    (state, { payload }): RemoteDiagnosticsState => ({
      ...state,
      connectionStatus: payload,
    })
  ),
  on(
    RemoteDiagnosticsActions.saveConfirmPopupFlag.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      isUseCasePopupBlocked: true,
    })
  ),

  on(
    RemoteDiagnosticsActions.getConfirmPopupFlag.attempt,
    RemoteDiagnosticsActions.saveConfirmPopupFlag.error,

    (state): RemoteDiagnosticsState => ({
      ...state,
      isUseCasePopupBlocked: false,
    })
  ),

  on(
    RemoteDiagnosticsActions.getConfirmPopupFlag.success,
    (state, { isUseCasePopupBlocked }): RemoteDiagnosticsState => ({
      ...state,
      isUseCasePopupBlocked,
    })
  ),

  on(
    RemoteDiagnosticsActions.setActiveConnectionStep,
    (state, { payload }): RemoteDiagnosticsState => ({
      ...state,
      activeConnectionStep: payload,
    })
  ),

  on(
    RemoteDiagnosticsActions.saveConnectionReason,
    (state, { connectionReason }): RemoteDiagnosticsState => ({
      ...state,
      connectionReason,
    })
  ),

  on(
    RemoteDiagnosticsActions.disconnect.failed,
    RemoteDiagnosticsActions.refreshActiveSessions.success,
    (state): RemoteDiagnosticsState => ({
      ...state,
      sessionStatusChecked: false,
    })
  ),

  on(
    RemoteDiagnosticsActions.diagstackGetFreeInstance.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      loading: true,
    })
  ),

  on(
    RemoteDiagnosticsActions.diagstackGetFreeInstance.failed,
    (state): RemoteDiagnosticsState => ({
      ...state,
      loading: false,
    })
  ),

  on(
    RemoteDiagnosticsActions.diagstackGetFreeInstance.success,
    (state, { gatewaySerial, deviceSerial, diagStackNode }): RemoteDiagnosticsState => {
      const sessionsWithoutActive = state.activeSessions.filter(
        (session) => session.gatewaySerial !== gatewaySerial
      );

      return {
        ...state,
        loading: false,
        activeSessions: sessionsWithoutActive.concat({
          xAuthToken: null,
          sessionGuid: null,
          diagStackNode: diagStackNode.hostAndPath,
          gatewaySerial,
          deviceSerial,
          selectedParameters: [],
          vciName: null,
          vciResource: null,
          vinNumber: null,
          technicianWsUrl: null,
          currentProductMatrix: null,
          mainLogicalLinkFromTopology: null,
          busDetailsFromTopology: [],
          activeLogicalLinkNames: [],
          availableControlUnits: [],
          timestamp: null,
          lastUserActivityTimestamp: null,
          user: null,
          selectedControlUnit: null,
          availableParametersForSelectedControlUnit: null,
          selectedExtendedParametersTab: ExtendedParametersTabsEnum.SearchedParameters,
          selectedUseCaseType: null,
          selectedUseCaseGroupe: null,
          bundleId: null,
          setupInfo: null,
        }),
      };
    }
  ),

  on(
    RemoteDiagnosticsActions.login.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeConnectionStep: DIAGNOSTICS_CONNECTION_STEP.LOGIN,
      connectionStatus: DIAGNOSTICS_CONNECTION_STATUS.LOADING,
    })
  ),

  on(
    RemoteDiagnosticsActions.login.success,
    (state, { gatewaySerial, remoteDiagnosticsSession, loginId }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            xAuthToken: remoteDiagnosticsSession,
            timestamp: moment.utc().format(),
            lastUserActivityTimestamp: moment.utc().format(),
            user: loginId,
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.openSession.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeConnectionStep: DIAGNOSTICS_CONNECTION_STEP.OPEN_SESSION,
    })
  ),

  on(
    RemoteDiagnosticsActions.openSession.success,
    (state, { gatewaySerial, guid }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            sessionGuid: guid,
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.setupInfo.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeConnectionStep: DIAGNOSTICS_CONNECTION_STEP.SETUP_INFO,
    })
  ),

  on(
    RemoteDiagnosticsActions.setupInfo.success,
    (state, { gatewaySerial, setupInfo }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            setupInfo,
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.remoteVciRegister.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeConnectionStep: DIAGNOSTICS_CONNECTION_STEP.REMOTE_VCI_REGISTER,
    })
  ),

  on(
    RemoteDiagnosticsActions.remoteVciRegister.success,
    (state, { gatewaySerial, technicianWsUrl }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return { ...session, technicianWsUrl };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.remoteVciAvailabilityCheck.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeConnectionStep: DIAGNOSTICS_CONNECTION_STEP.REMOTE_VCI_AVAILABILITY_CHECK,
    })
  ),

  on(
    RemoteDiagnosticsActions.remoteVciAvailabilityCheck.success,
    (state, { gatewaySerial, remoteVciName, resource, vinNumber }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return { ...session, vciName: remoteVciName, vciResource: resource, vinNumber };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.vciConnect.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeConnectionStep: DIAGNOSTICS_CONNECTION_STEP.REMOTE_VCI_ESTABLISHING_CONNECTION,
    })
  ),

  on(
    RemoteDiagnosticsActions.fetchProductMatrix.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeConnectionStep: DIAGNOSTICS_CONNECTION_STEP.PRODUCT_MATRIX_FETCH,
    })
  ),

  on(
    RemoteDiagnosticsActions.fetchProductMatrix.success,
    (
      state,
      { gatewaySerial, filteredProductMatrix: currentProductMatrix }
    ): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            currentProductMatrix,
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.mainLogicalLinkOpen.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeConnectionStep: DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_OPEN_ATTEMPT,
    })
  ),

  on(
    RemoteDiagnosticsActions.mainLogicalLinkClose.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeConnectionStep: DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_CLOSE_ATTEMPT,
    })
  ),

  on(
    RemoteDiagnosticsActions.logicalLinkOpen.success,
    RemoteDiagnosticsActions.mainLogicalLinkOpen.success,
    RemoteDiagnosticsActions.mainLogicalLinkFromTopologyOpen.success,
    RemoteDiagnosticsActions.openLogicalLinkBeforeVddReading.success,
    (state, { gatewaySerial, logicalLinkName }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            activeLogicalLinkNames: session.activeLogicalLinkNames.concat(logicalLinkName),
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.mainLogicalLinkClose.success,
    (state, { gatewaySerial, logicalLinkName }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            activeLogicalLinkNames: session.activeLogicalLinkNames.filter(
              (ll) => logicalLinkName !== ll
            ),
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.loadActiveSessions.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeSessionStatusChecked: false,
    })
  ),
  on(
    RemoteDiagnosticsActions.loadActiveSessions.failed,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeSessionStatusChecked: true,
    })
  ),
  on(
    RemoteDiagnosticsActions.loadActiveSessions.success,
    (state, { activeSessions }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions,
      activeSessionStatusChecked: true,
    })
  ),

  on(
    RemoteDiagnosticsActions.updateActiveSessions,
    (state, { activeSessions }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions,
    })
  ),

  on(
    RemoteDiagnosticsActions.fetchDeviceTopology.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeConnectionStep: DIAGNOSTICS_CONNECTION_STEP.SCAN_TOPOLOGY,
    })
  ),

  on(
    DiagStackDtcActions.getActiveDtc.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeConnectionStep: DIAGNOSTICS_CONNECTION_STEP.ACTIVE_DTC_FETCH,
    })
  ),

  on(
    RemoteDiagnosticsActions.mainLogicalLinkFromTopologyOpen.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeConnectionStep:
        DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_FROM_TOPOLOGY_OPEN_ATTEMPT,
    })
  ),

  on(
    RemoteDiagnosticsActions.mainLogicalLinkFromTopologyOpen.skipped,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeConnectionStep: DIAGNOSTICS_CONNECTION_STEP.CONNECTION_ACTIVE,
    })
  ),

  on(
    RemoteDiagnosticsActions.saveMainLogicalLinkFromTopology,
    (state, { gatewaySerial, logicalLinkName }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            mainLogicalLinkFromTopology: logicalLinkName,
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.saveBundleIdFromTopology,
    (state, { gatewaySerial, bundleId }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            bundleId,
          };
        }

        return session;
      }),
    })
  ),
  on(
    RemoteDiagnosticsActions.saveBusDetailsFromTopology.success,
    RemoteDiagnosticsActions.updateBusDetailsFromDtc.success,
    (state, { gatewaySerial, busDetailsFromTopology }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            busDetailsFromTopology,
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.loadControlUnits.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      activeConnectionStep: DIAGNOSTICS_CONNECTION_STEP.LOAD_CONTROL_UNITS,
    })
  ),

  on(
    RemoteDiagnosticsActions.loadControlUnits.success,
    (state, { gatewaySerial, controlUnits }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            availableControlUnits: session.availableControlUnits.concat(controlUnits),
          };
        }

        return session;
      }),
      sessionStatusChecked: false,
    })
  ),

  on(
    RemoteDiagnosticsActions.loadVddList.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      loading: true,
    })
  ),

  on(
    RemoteDiagnosticsActions.loadControlUnits.failed,
    (state): RemoteDiagnosticsState => ({
      ...state,
      loading: false,
    })
  ),

  on(
    RemoteDiagnosticsActions.loadVddList.success,
    (state, { gatewaySerial, parameters }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            availableParametersForSelectedControlUnit: parameters,
          };
        }

        return session;
      }),
      loading: false,
    })
  ),

  on(
    RemoteDiagnosticsActions.selectControlUnit,
    (state, { gatewaySerial, controlUnit }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedControlUnit: controlUnit,
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.parameter.add,
    (state, { controlUnit, vdd, gatewaySerial }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedParameters: session.selectedParameters.concat({
              id: (session.selectedParameters[session.selectedParameters.length - 1]?.id ?? 0) + 1,
              controlUnit,
              vddDetail: vdd,
              requestId: '',
              interval: DIAGNOSTICS_SUPPORTED_INTERVALS.s1,
              isSelected: false,
            } as RemoteDiagnosticsParameter),
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.parameter.delete,
    (state, { parameterId, gatewaySerial }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedParameters: session.selectedParameters.filter(
              (parameter) => parameter.id !== parameterId
            ),
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.parameter.change,
    (state, { parameter, gatewaySerial }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedParameters: session.selectedParameters.map((param) => {
              if (param.id === parameter.id) {
                return param;
              }

              return param;
            }),
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.allParameters.unselect,
    (state, { gatewaySerial }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedParameters: session.selectedParameters.map((parameter) => ({
              ...parameter,
              isSelected: false,
            })),
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.allParameters.select,
    (state, { gatewaySerial }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedParameters: session.selectedParameters.map((parameter) => ({
              ...parameter,
              isSelected: true,
            })),
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.allParameters.changeInterval,
    (state, { interval, gatewaySerial }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedParameters: session.selectedParameters.map((parameter) => {
              if (parameter.requestId === '' && parameter.isSelected) {
                return {
                  ...parameter,
                  interval,
                };
              }

              return parameter;
            }),
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.deleteSelectedParameters,
    (state, { gatewaySerial }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedParameters: session.selectedParameters.filter(
              (parameter) => !parameter.isSelected || parameter.requestId !== '' // never delete running ones, they should be stopped first
            ),
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.deleteUseCaseParameters,
    (state, { gatewaySerial }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedParameters: session.selectedParameters.filter(
              (parameter) => !parameter.vddDetail.isUseCaseVdd
            ),
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.scheduleParameterReadCycle.success,
    (state, { parameterId, requestId, gatewaySerial }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedParameters: session.selectedParameters.map((parameter) => {
              if (parameter.id === parameterId) {
                return {
                  ...parameter,
                  requestId,
                };
              }

              return parameter;
            }),
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.cancelParameterReadCycle.success,
    (state, { parameterId, gatewaySerial }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedParameters: session.selectedParameters.map((parameter) => {
              if (parameter.id === parameterId) {
                return {
                  ...parameter,
                  requestId: '',
                };
              }

              return parameter;
            }),
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.cancelMultipleParametersReadCycle.attempt,
    (state): RemoteDiagnosticsState => {
      return {
        ...state,
        cancelMultipleParametersReadCycleInProgress: true,
      };
    }
  ),

  on(
    RemoteDiagnosticsActions.cancelMultipleParametersReadCycle.failed,
    RemoteDiagnosticsActions.cancelMultipleParametersReadCycle.aborted,
    (state): RemoteDiagnosticsState => {
      return {
        ...state,
        cancelMultipleParametersReadCycleInProgress: false,
      };
    }
  ),

  on(
    RemoteDiagnosticsActions.cancelMultipleParametersReadCycle.outcome,
    (state, { gatewaySerial, transactionOutcome }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedParameters: session.selectedParameters.map((parameter) => {
              if (transactionOutcome[parameter.id] !== undefined) {
                return {
                  ...parameter,
                  requestId: '',
                };
              }

              return parameter;
            }),
          };
        }

        return session;
      }),
      cancelMultipleParametersReadCycleInProgress: false,
    })
  ),

  on(
    RemoteDiagnosticsActions.scheduleMultipleParametersReadCycle.attempt,
    (state): RemoteDiagnosticsState => {
      return {
        ...state,
        scheduleMultipleParametersReadCycleInProgress: true,
      };
    }
  ),

  on(
    RemoteDiagnosticsActions.scheduleMultipleParametersReadCycle.failed,
    (state): RemoteDiagnosticsState => {
      return {
        ...state,
        scheduleMultipleParametersReadCycleInProgress: false,
      };
    }
  ),

  on(
    RemoteDiagnosticsActions.scheduleMultipleParametersReadCycle.outcome,
    (state, { gatewaySerial, transactionOutcome }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedParameters: session.selectedParameters.map((parameter) => {
              if (transactionOutcome[parameter.id] !== undefined) {
                return {
                  ...parameter,
                  requestId: transactionOutcome[parameter.id],
                };
              }

              return parameter;
            }),
          };
        }

        return session;
      }),
      scheduleMultipleParametersReadCycleInProgress: false,
    })
  ),

  on(
    RemoteDiagnosticsActions.logicalLinkList.success,
    (state, { logicalLinkList }): RemoteDiagnosticsState => ({
      ...state,
      logicalLinkList,
      sessionStatusChecked: true,
    })
  ),

  on(
    RemoteDiagnosticsActions.logicalLinkList.failed,
    (state): RemoteDiagnosticsState => ({
      ...state,
      logicalLinkList: null,
      sessionStatusChecked: true,
    })
  ),

  on(
    RemoteDiagnosticsActions.lastUserActivityTimestampReset,
    (state, { gatewaySerial }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return { ...session, lastUserActivityTimestamp: moment().utc().format() };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsUseCasesActions.loadUseCaseVddListAttempt,
    (state, { gatewaySerial }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            selectedParameters: session.selectedParameters.filter(
              (parameter) => !parameter.vddDetail.isUseCaseVdd
            ),
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsUseCasesActions.useCaseTypeChanged,
    (state, { gatewaySerial, useCaseType }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedUseCaseType: useCaseType,
            selectedUseCaseGroupe: null,
            selectedParameters: session.selectedParameters.filter(
              (parameter) => !parameter.vddDetail.isUseCaseVdd
            ),
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsUseCasesActions.extendedParametersTabChanged,
    (state, { gatewaySerial, tab }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedExtendedParametersTab: tab,
            selectedUseCaseType: null,
            selectedUseCaseGroupe: null,
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsUseCasesActions.useCaseGroupChanged,
    (state, { gatewaySerial, useCaseGroupe }): RemoteDiagnosticsState => ({
      ...state,
      activeSessions: state.activeSessions.map((session) => {
        if (session.gatewaySerial === gatewaySerial) {
          return {
            ...session,
            lastUserActivityTimestamp: moment().utc().format(),
            selectedUseCaseGroupe: useCaseGroupe,
            selectedParameters: session.selectedParameters.filter(
              (parameter) => !parameter.vddDetail.isUseCaseVdd
            ),
          };
        }

        return session;
      }),
    })
  ),

  on(
    RemoteDiagnosticsActions.logicalLinkOpen.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      logicalLinkOpenStatus: {
        loading: true,
        error: null,
      },
    })
  ),

  on(
    RemoteDiagnosticsActions.logicalLinkOpen.abort,
    (state): RemoteDiagnosticsState => ({
      ...state,
      logicalLinkOpenStatus: {
        loading: false,
        error: null,
      },
    })
  ),

  on(
    RemoteDiagnosticsActions.logicalLinkOpen.success,
    (state): RemoteDiagnosticsState => ({
      ...state,
      logicalLinkOpenStatus: {
        loading: false,
        error: null,
      },
    })
  ),

  on(
    RemoteDiagnosticsActions.logicalLinkOpen.failed,
    (state, { error }): RemoteDiagnosticsState => ({
      ...state,
      logicalLinkOpenStatus: {
        loading: false,
        error,
      },
    })
  ),

  on(
    RemoteDiagnosticsActions.loadVddList.attempt,
    (state): RemoteDiagnosticsState => ({
      ...state,
      vddListStatus: {
        loading: true,
        error: null,
      },
    })
  ),

  on(
    RemoteDiagnosticsActions.loadVddList.success,
    (state): RemoteDiagnosticsState => ({
      ...state,
      vddListStatus: {
        loading: false,
        error: null,
      },
    })
  ),

  on(
    RemoteDiagnosticsActions.loadVddList.failed,
    (state, { error }): RemoteDiagnosticsState => ({
      ...state,
      vddListStatus: {
        loading: false,
        error,
      },
    })
  ),

  on(
    RemoteDiagnosticsActions.manageChartCollapsibility.hide,
    (state): RemoteDiagnosticsState => ({
      ...state,
      isChartUncollapsed: false,
    })
  ),
  on(
    RemoteDiagnosticsActions.manageChartCollapsibility.show,
    (state): RemoteDiagnosticsState => ({
      ...state,
      isChartUncollapsed: true,
    })
  ),
  on(
    RemoteDiagnosticsActions.manageChartCollapsibility.toggle,
    (state): RemoteDiagnosticsState => ({
      ...state,
      isChartUncollapsed: !state.isChartUncollapsed,
    })
  )
);
